'use client';

import Avatar from '@mui/material/Avatar';

import { LINE, RADIUS, SURFACE } from 'src/theme/ui-tokens';

/**
 * **브랜드 얼굴** — 둥근 네모 · 흰 바탕 · 그림을 자르지 않는다(`object-fit: contain`).
 *
 * 🔑 브랜드 얼굴은 대개 **가로로 긴 로고**다 — 동그라미에 꽉 채워 자르면 글자 끝이 잘려 못 읽는다.
 *   그래서 네모(모서리만 둥글게)에 흰 바탕을 깔고 그림 전체를 들인다.
 * ⛔ 사람·계정 얼굴에는 쓰지 마라 — 사람은 동그라미가 맞다(`Avatar` 그대로).
 * ⚠️ 그림이 없거나 못 불러오면 머리글자(이름 첫 글자)로 떨어진다 — 그때는 연한 색 바탕이다.
 *   `tone="neutral"` 이면 무채색 면 + 진한 글자다(주 색을 단추 하나에만 쓰는 화면 — 시작하기).
 */
export function HubBrandAvatar({
  src,
  name,
  size,
  typography = 'uiMeta',
  tone = 'primary',
}: {
  /** 얼굴 주소 — 없으면 빈 문자열 */
  src?: string;
  /** 브랜드 이름 — 머리글자와 대체 글이 된다 */
  name: string;
  /** 한 변(px) */
  size: number;
  /** 머리글자의 글꼴 단계 */
  typography?: 'uiMeta' | 'uiTitle' | 'uiHead';
  /** 머리글자 바탕 — `neutral` 은 무채색 */
  tone?: 'primary' | 'neutral';
}) {
  return (
    <Avatar
      variant="rounded"
      src={src || undefined}
      alt={name}
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: RADIUS.sm,
        typography,
        fontWeight: 600,
        ...(src
          ? { bgcolor: 'background.paper', color: 'primary.dark', border: '1px solid', borderColor: LINE.edge, '& img': { objectFit: 'contain' } }
          : tone === 'neutral'
            ? { bgcolor: SURFACE.sunken, color: 'text.primary', border: '1px solid', borderColor: LINE.edge }
            : { bgcolor: 'primary.lighter', color: 'primary.dark' }),
      }}
    >
      {name.trim().slice(0, 1).toUpperCase()}
    </Avatar>
  );
}
